import {
    CdkOverlayOrigin,
    ConnectionPositionPair,
    Overlay,
    OverlayModule,
    ScrollStrategy,
} from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    input,
    InputSignal,
    OnInit,
    Output,
    signal,
    Signal,
    untracked,
    viewChild,
    WritableSignal,
    OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import {
    IqBuilderLabelComponent,
    IqStyleProcessorCssClassComponent,
    mdIconLabel,
} from '@dat/iq-style-processor';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';

import { DatiqSelectModule } from '@dat/datiq-components-pkg/select';
import { IqControlsControlPanelFormComponent } from '../iq-controls-control-panel-form/iq-controls-control-panel-form.component';
import { Control } from '../models';
import { IqControlsStatusService } from '../state';

@Component({
    selector: 'iq-controls-control-panel-wrapper',
    standalone: true,
    templateUrl: './iq-controls-control-panel-wrapper.component.html',
    styleUrl: './iq-controls-control-panel-wrapper.component.scss',
    imports: [
        CommonModule,
        DatiqSelectModule,
        OverlayModule,
        DatiqTemplateModule,
        IqControlsControlPanelFormComponent,
        IqStyleProcessorCssClassComponent,
        IqBuilderLabelComponent,
    ],
})
export class IqControlsControlPanelWrapperComponent
    implements OnInit, OnDestroy
{
    private readonly _cdkOverlayService = inject(Overlay);
    private readonly _fb = inject(FormBuilder);

    trigger: Signal<CdkOverlayOrigin> = viewChild('trigger');

    overlayPositions: ConnectionPositionPair[] = [
        new ConnectionPositionPair(
            { originX: 'start', originY: 'bottom' },
            { overlayX: 'start', overlayY: 'top' },
        ),
    ];
    scrollStrategy: ScrollStrategy =
        this._cdkOverlayService.scrollStrategies.reposition();

    @Input()
    fg: FormGroup;

    panelFg: FormGroup = this._fb.group({});

    /* Input Signals */
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    name: InputSignal<string> = input('');
    formClasses: InputSignal<any> = input({}); // TODO: Add type to leverage the new IqRuelsData Schema
    panelClasses: InputSignal<any> = input({}); // TODO: Add type to leverage the new IqRuelsData Schema
    controls: InputSignal<Control[]> = input([]);
    openLabel: InputSignal<string> = input('');
    closedLabel: InputSignal<string> = input('');
    disabledLabel: InputSignal<string> = input('');
    isDisabled: InputSignal<boolean> = input(false);
    isEdited: InputSignal<boolean> = input(false);

    /* Writable Signals */
    // Value should be toggled when component is clicked
    triggerOpen: WritableSignal<boolean> = signal(false);

    /* Computed Signals */
    currentTestId: Signal<string> = computed(() => {
        return !!this.testId()?.length ? this.testId() : this.id();
    });
    isOpened = computed(() => {
        return this.triggerOpen();
    });

    formId: Signal<string> = computed(() => {
        return `${this.id()}-form`;
    });

    currentLabel: Signal<string> = computed(() => {
        const statusLabel: string = this.isDisabled()
            ? this.disabledLabel()
            : this.isOpened()
            ? this.openLabel()
            : this.closedLabel();
        const arrowLabel: string = this.isOpened()
            ? mdIconLabel('arrow_drop_up', '', false, {
                  classes: ['inline-block', 'pl-2', 'pr-1'],
              })
            : mdIconLabel('arrow_drop_down', '', false, {
                  classes: ['inline-block', 'pl-2', 'pr-1'],
              });

        return `${statusLabel}  ${arrowLabel}`;
    });

    triggerStatus: Signal<'edited' | 'unedited' | 'disabled'> = computed(() => {
        return this.isDisabled()
            ? 'disabled'
            : this.isEdited()
            ? 'edited'
            : 'unedited';
    });

    /* Outputs */
    @Output()
    clicked = new EventEmitter<any>();
    @Output()
    changed = new EventEmitter<any>();
    @Output()
    submitted = new EventEmitter<any>();
    @Output()
    resetted = new EventEmitter<any>();
    @Output()
    opened = new EventEmitter<any>();
    @Output()
    closed = new EventEmitter<any>();

    currentControls: Signal<Control[]> = computed(() => {
        return this._status.getControls(this.formId())();
    });

    private _deregister: Record<string, IqEventBusBrokerHandler> = {};

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    constructor() {
        this._deregister = this._bus.registerMap({
            'type:submit': {
                callback: (eventInfo) => {
                    const submitControl = this.controls().find((control) => {
                        return control.type === 'Submit';
                    });

                    if (!submitControl) return;

                    const submitControlId: string = submitControl.id;
                    const eventInfoId: string = eventInfo.id;

                    if (submitControlId !== eventInfoId) return;

                    this.submit();
                },
            },
        });

        effect(() => {
            // !DEBUGGING CODE DON'T REMOVE
            // console.table(this.controls());
            // console.log(this.currentControls());
            // !DEBUGGING CODE DON'T REMOVE
        });

        effect(() => {
            const propControls = this.controls();

            untracked(() => {
                this._status.replaceControls(this.formId(), propControls);
            });
        });
    }

    private readonly _status = inject(IqControlsStatusService);
    // private readonly _action = inject(IqWorkflowActionProcessorService);

    ngOnInit() {
        if (!this._status.hasFg(this.formId())) {
            this._status.registerFormGroup(this.formId(), this.panelFg);
            const validControls: string[] = [
                'Select',
                'Tabs',
                'Percent',
                'Integer',
                'Currency',
                'Geo',
                'Checkbox',
            ];
            this.controls().forEach((control) => {
                if (validControls.includes(control.type)) {
                    this.panelFg.addControl(
                        control.name,
                        this._fb.control((control as any).value),
                    );
                }
            });
        } else {
            this.panelFg = this._status.getFg(this.formId());
        }

        this.fg?.addControl(this.name(), this.panelFg);
    }

    ngOnDestroy() {
        this._bus.unregisterMap(this._deregister);
    }

    click(...args): void {
        // const emitData = this.createEmitData(args);
        // this.clicked.emit(emitData);
        // this._runLocalActions(
        //     emitData,
        //     emitData?.value?.control?.onClick ?? [],
        // );
    }

    change(...args): void {
        // const emitData: Record<string, any> = this.createEmitData(args);
        // this.changed.emit(emitData.value.form);
        // this._runLocalActions(
        //     emitData,
        //     emitData?.value?.control?.onChange ?? [],
        // );
    }

    // private _runLocalActions(
    //     emitData: Record<string, any>,
    //     actions: IDatiqRulesData<IDatiqAction>[],
    // ) {
    //     // if (!actions.length) return;

    //     // this._action.processActions(actions, emitData);
    // }

    submit(...args): void {
        // const emitData = this.createEmitData(args);
        // this.submitted.emit(emitData);
        this.pressEscape();
    }

    reset(...args): void {
        // const emitData = this.createEmitData(args);
        // this.resetted.emit(emitData);
    }

    open(...args): void {
        this.triggerOpen.set(true);

        /**
         * This dispatches an event indicating the panel has been opened.
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onPanelOpen": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "PANEL_ID"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */

        const displatch = () =>
            this._bus.dispatch({
                name: 'type:panel:open',
                payload: {
                    id: this.id(),
                    ...this.createEmitData(args),
                },
            });

        setTimeout(() => {
            displatch();
        }, 1);
    }

    close(...args): void {
        this.triggerOpen.set(false);

        /**
         * This dispatches an event indicating the panel was closed.
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onPanelClose": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "PANEL_ID"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */

        const displatch = () =>
            this._bus.dispatch({
                name: 'type:panel:close',
                payload: {
                    id: this.id(),
                    ...this.createEmitData(args),
                },
            });

        setTimeout(() => {
            displatch();
        }, 1);
    }

    openPanel(): void {
        if (this.isOutsideClick()) return;

        this.isOpened() ? this.close() : this.open();
    }

    pressEscape(): void {
        this.triggerOpen.set(false);
    }

    isOutsideClick: WritableSignal<boolean> = signal(false);

    outsideClick(): void {
        this.isOutsideClick.set(true);
        setTimeout(() => {
            this.close();
            this.isOutsideClick.set(false);
        });
    }

    private createEmitData(args: any[]): Record<string, any> {
        const templateProps: Record<string, any> =
            args?.reduce(
                (
                    currentProps: Record<any, number>,
                    key: any,
                    index: number,
                ) => {
                    return index % 2 === 0
                        ? {
                              ...currentProps,
                              [key]: args?.[index + 1] ?? null,
                          }
                        : currentProps;
                },
                {},
            ) ?? {};

        const formCacheData = this._status.getFormCache(this.formId());
        const emitData: Record<string, any> = {
            form: {
                ...this.panelFg.value,
                valid: this.panelFg.valid,
                errors: this.panelFg.errors,
            },
            formCache: formCacheData,
            ...templateProps,
        };
        return emitData;
    }
}
